import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

interface OrderItem {
  id: string;
  quantity: number;
  points_price: number;
  product: {
    name: string;
    image: string;
  };
}

interface Order {
  id: string;
  created_at: string;
  status: string;
  points_used: number;
  items: OrderItem[];
}

const statusColors: Record<string,string> = {
  pending: 'bg-yellow-500',
  processing: 'bg-blue-500',
  shipped: 'bg-purple-500',
  delivered: 'bg-green-500',
  cancelled: 'bg-red-500',
};
// Status labels will be handled by translations

const OrderHistory: React.FC = () => {
  const { t } = useLanguage();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string| null>(null);
  const { user, userProfile } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const { data: ords, error } = await supabase
        .from('orders')
        .select('id,created_at,status,points_used')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      const withItems = await Promise.all(ords!.map(async o => {
        const { data: items, error: ie } = await supabase
          .from('order_items')
          .select('id,quantity,points_price,product:product_id(name,image)')
          .eq('order_id', o.id);
        if (ie) throw ie;
        return { ...o, items: items! };
      }));
      setOrders(withItems);
    } catch (e:any) {
      toast({ title: t('orders.loadError'), description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id:string)=> setOpenId(openId===id?null:id);
  const fmtDate=(ds:string)=> new Date(ds).toLocaleDateString('pt-BR');

  if (loading) return <div className="flex justify-center p-8"><div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-red"></div></div>;
  if (!orders.length) return (
    <Card className="bg-gray-800 text-white border-gray-700">
      <CardHeader>
        <CardTitle>{t('orders.noOrdersTitle')}</CardTitle>
        <CardDescription className="text-gray-300">{t('orders.noOrdersDesc')}</CardDescription>
      </CardHeader>
      <CardContent><p className="text-center py-6 text-gray-400">{t('orders.noOrdersMessage')}</p></CardContent>
    </Card>
  );

  // calcular saldo antes/depois
  let running = userProfile?.points || 0;
  const list = orders.map(o => {
    const before = running;
    running -= o.points_used!;  // Subtract points used from current balance
    const after = running;
    return { ...o, before, after };
  });

  return (
    <Card className="bg-gray-800 text-white border-gray-700">
      <CardHeader>
        <CardTitle>{t('orders.title')}</CardTitle>
        <CardDescription className="text-gray-300">{t('orders.recentOrdersDesc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {list.map(o => (
          <div key={o.id} className="bg-gray-900 rounded-lg overflow-hidden">
            <div className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <p className="font-medium">{t('orders.orderNumber').replace('{orderNumber}', o.id.slice(0,8))}</p>
                  <p className="text-sm text-gray-400">{fmtDate(o.created_at)}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={()=>toggle(o.id)} className="ml-2 flex-shrink-0">
                  {openId===o.id?<ChevronUp size={18}/>:<ChevronDown size={18}/>}
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Badge className={`${statusColors[o.status]} text-white text-xs px-2 py-1`}>
                    {t(`orders.${o.status}`) || o.status}
                  </Badge>
                  <p className="font-medium text-sm">{o.points_used} storepoints</p>
                </div>
              </div>
            </div>
            {openId===o.id && (
              <div className="p-4 pt-0">
                <Separator className="mb-4 bg-gray-700" />
                <div>
                  <p className="text-sm text-gray-400">{t('orders.balanceBefore')}: {o.before} storepoints</p>
                  <p className="text-sm text-gray-400 mb-4">{t('orders.balanceAfter')}: {o.after} storepoints</p>

                  {/* Show payment status messages */}
                  {o.admin_notes && (
                    <div className={`p-3 rounded mb-4 ${o.status === 'payment_failed' ? 'bg-red-900/20 border border-red-700' : 'bg-blue-900/20 border border-blue-700'}`}>
                      <p className="text-sm">
                        {o.admin_notes === 'PAYMENT_CONFIRMED'
                          ? t('orders.paymentConfirmed')
                          : o.admin_notes === 'PAYMENT_FAILED_UNKNOWN'
                          ? t('orders.paymentFailedUnknown')
                          : t(`storePointConnector.errors.${o.admin_notes}`) || t('orders.paymentFailed')}
                      </p>
                    </div>
                  )}

                  {o.items.map(it=>(
                    <div key={it.id} className="flex space-x-4 mb-2">
                      <div className="h-16 w-16 bg-white rounded overflow-hidden">
                        <img src={it.product.image} alt={it.product.name} className="object-contain w-full h-full"/>
                      </div>
                      <div className="flex-1">
                        <p>{it.product.name}</p>
                        <p className="text-sm text-gray-400">{t('orders.quantity').replace('{quantity}', it.quantity.toString())}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default OrderHistory;
